import Loading from "@lhd/components/Loading";
import { ROUTES } from "@lhd/constants";
import { useFetchOneClientV2, useGetAuth, useGetPermissions } from "@lhd/hooks";
import { Company } from "@lhd/types";
import { toString } from "lodash";
import { createContext, FC, ReactNode, useMemo } from "react";
import { Navigate, useParams } from "react-router-dom";

interface Props {
  children: ReactNode;
}

const STATUS_ENUM = {
  LOADING: "loading",
  ERROR: "error",
  SUCCESS: "success",
};

export const CustomerHistoryContext = createContext<{
  data: Company | null;
  loading: boolean;
}>({
  data: null,
  loading: true,
});

const CustomerHistoryProtectedRoute: FC<Props> = ({ children }) => {
  const { id: clientId } = useParams();
  const { isUserResigned } = useGetAuth();
  const {
    data: clientData,
    status,
    isError,
  } = useFetchOneClientV2(toString(clientId));

  const permission = useGetPermissions();

  const permissionState = useMemo(() => {
    if (status === STATUS_ENUM.LOADING) {
      return {
        isLoading: true,
        data: null,
        shouldRedirect: permission[ROUTES.CUSTOMER_LIST] ? false : true,
      };
    }

    if (status === STATUS_ENUM.ERROR || isError) {
      return {
        isLoading: false,
        data: null,
        shouldRedirect: true,
      };
    }

    if (status === STATUS_ENUM.SUCCESS) {
      if (!clientData) {
        return {
          isLoading: false,
          data: null,
          shouldRedirect: true,
        };
      }
      return {
        isLoading: false,
        data: clientData as Company,
        shouldRedirect: permission[ROUTES.CUSTOMER_LIST] ? false : true,
      };
    }

    return {
      isLoading: true,
      data: null,
      shouldRedirect: permission[ROUTES.CUSTOMER_LIST] ? false : true,
    };
  }, [status, clientData, isError, permission]);

  if (permissionState.isLoading) {
    return <Loading />;
  }

  if (permissionState.shouldRedirect || isUserResigned) {
    return <Navigate to={`/${ROUTES.NOT_FOUND_PAGE}`} replace />;
  }

  return (
    <CustomerHistoryContext.Provider
      value={{
        data: permissionState.data as Company,
        loading: permissionState.isLoading,
      }}
    >
      {children}
    </CustomerHistoryContext.Provider>
  );
};

export default CustomerHistoryProtectedRoute;
